import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddressOptionsQueryDTO,
  CustomerListQueryDTO,
  CustomerOrdersQueryDTO,
  CustomerProductAffinityQueryDTO,
  MergeCustomersDTO,
  UpdateCodRiskSettingsDTO,
  UpdateCustomerDTO,
} from "@/modules/customers/domain/contracts";
import { customersApi } from "./client";

const keys = {
  all: ["customers"] as const,
  list: (query?: CustomerListQueryDTO) =>
    [...keys.all, "list", query] as const,
  search: (q: string) => [...keys.all, "search", q] as const,
  addressOptions: (query: AddressOptionsQueryDTO) =>
    [...keys.all, "address-options", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  orders: (id: string, query?: CustomerOrdersQueryDTO) =>
    [...keys.all, "orders", id, query] as const,
  risk: (id: string, proposedTotal?: number) =>
    [...keys.all, "risk", id, proposedTotal ?? null] as const,
  productAffinity: (id: string, query?: CustomerProductAffinityQueryDTO) =>
    [...keys.all, "product-affinity", id, query] as const,
  riskSettings: [...["customers"], "risk-settings"] as const,
};

export function useCustomersQuery(query?: CustomerListQueryDTO) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => customersApi.list(query),
  });
}

export function useCustomerSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => customersApi.search(q),
    enabled: q.trim().length >= 2,
  });
}

export function useAddressOptionsQuery(
  query: AddressOptionsQueryDTO,
  enabled = true,
) {
  const ready =
    enabled &&
    (query.level === "province" ||
      (query.level === "district" && Boolean(query.province?.trim())) ||
      (query.level === "village" &&
        Boolean(query.province?.trim()) &&
        Boolean(query.district?.trim())));

  return useQuery({
    queryKey: keys.addressOptions(query),
    queryFn: () => customersApi.addressOptions(query),
    enabled: ready,
  });
}

export function useCustomerQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => customersApi.get(id),
    enabled: Boolean(id),
  });
}

export function useCustomerOrdersQuery(
  id: string,
  query?: CustomerOrdersQueryDTO,
) {
  return useQuery({
    queryKey: keys.orders(id, query),
    queryFn: () => customersApi.listOrders(id, query),
    enabled: Boolean(id),
  });
}

export function useCustomerRiskQuery(
  id: string | undefined,
  proposedTotal?: number | null,
) {
  const total =
    proposedTotal != null && Number.isFinite(proposedTotal)
      ? proposedTotal
      : undefined;
  return useQuery({
    queryKey: keys.risk(id ?? "", total),
    queryFn: () => customersApi.risk(id!, { proposedTotal: total }),
    enabled: Boolean(id),
  });
}

export function useCustomerProductAffinityQuery(
  id: string,
  query?: CustomerProductAffinityQueryDTO,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.productAffinity(id, query),
    queryFn: () => customersApi.productAffinity(id, query),
    enabled: options?.enabled ?? Boolean(id),
  });
}

export function useCodRiskSettingsQuery() {
  return useQuery({
    queryKey: keys.riskSettings,
    queryFn: () => customersApi.getRiskSettings(),
  });
}

export function useUpdateCodRiskSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCodRiskSettingsDTO) =>
      customersApi.updateRiskSettings(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.riskSettings });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCustomerDTO) => customersApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useCustomerMergePreviewQuery(
  input: { sourceId: string; targetId: string } | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...keys.all, "merge-preview", input] as const,
    queryFn: () => customersApi.mergePreview(input!),
    enabled:
      enabled &&
      Boolean(input?.sourceId && input?.targetId && input.sourceId !== input.targetId),
  });
}

export function useMergeCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MergeCustomersDTO) => customersApi.merge(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["custom-jobs"] });
      qc.invalidateQueries({ queryKey: ["restock-waitlist"] });
    },
  });
}
